<template>
  <v-dialog
    v-if="!inline"
    v-model="isVisible"
    :max-width="maxWidth"
    :attach="attach"
    @click:outside="handleClose"
    @keydown.esc="handleClose"
    persistent
  >
    <v-card
      class="responsibility-suggestion-modal"
      :class="{ 'edit-mode-background': isEditMode }"
    >
      <!-- QuadrilleBackground for Edit Mode -->
      <QuadrilleBackground v-if="isEditMode" className="background-popup" />
      
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content-wrapper">
          <div class="header-content" :class="{ 'edit-mode-header': isEditMode }">
            <!-- Edit Mode Header (Icon before Title) -->
            <template v-if="isEditMode">
              <!-- Featured Icon -->
              <div class="featured-icon">
                <Icon name="org_icon_responsibility" />
              </div>
              <div class="header-text">
                <v-card-title class="modal-title">
                  Responsibility Suggestion ({{ currentIndex }}/{{ totalCount }})
                </v-card-title>
              </div>
            </template>
            
            <!-- Navigate Mode Header (Icon before Title) -->
            <template v-else>
              <!-- Featured Icon -->
              <div class="featured-icon">
                <Icon name="org_icon_responsibility" />
              </div>
              <!-- Title and Description -->
              <div class="header-text">
                <v-card-title class="modal-title">
                  {{
                    isAllDone
                      ? "All suggestions were processed"
                      : `${totalCount} Suggested ${
                          totalCount === 1 ? "Responsibility" : "Responsibilities"
                        } for Role ${roleName}`
                  }}
                </v-card-title>
                <v-card-text class="modal-description">
                  {{
                    isAllDone
                      ? "Close the right pane or ask for more suggestions"
                      : description
                  }}
                </v-card-text>
              </div>
            </template>
          </div>
        </div>

        <!-- Close Button -->
        <v-btn icon @click="handleClose" color="#98A2B3" class="modal-close-btn-suggestion">
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <!-- Bottom spacing -->
        <div class="header-bottom-spacing"></div>
      </div>

      <!-- Completion State (when all responsibilities are done) -->
      <div
        v-if="isAllDone"
        class="completion-state"
      >
        <div class="completion-actions">
          <div class="completion-actions-left">
            <StandardButton
              type="magenta"
              variant="outline"
              size="full-width"
              label="Suggest More"
              @click="handleSuggestMore"
            />
          </div>
          <div class="completion-actions-right">
            <StandardButton
              type="magenta"
              variant="primary"
              size="full-width"
              label="Close"
              @click="handleClose"
            />
          </div>
        </div>
      </div>

      <!-- Normal Content (when there are responsibilities left) -->
      <template v-else>
        <!-- Navigation Actions - Navigate Mode -->
        <div v-if="!isEditMode" class="modal-actions">
          <div class="actions-content">
            <StandardButton
              type="magenta"
              variant="outline"
              size="normal"
              label="Edit"
              @click="handleEdit"
            />
            <div class="actions-left">
              <span class="counter-text"
                >{{ currentIndex }} of {{ totalCount }}</span
              >
            </div>

            <div class="actions-right">
              <!-- Back Button -->
              <StandardButton
                type="magenta"
                variant="outline"
                size="normal"
                label=""
                leftIcon="arrowLeft"
                @click="handlePrevious"
                :class="{ 'invisible-btn': currentIndex <= 1 }"
              />

              <!-- Create and Attach Button -->
              <StandardButton
                type="magenta"
                variant="primary"
                size="normal"
                label="Create and Attach"
                @click="handleCreateAndAttach"
                :loaderLeft="isCreating"
                :disabled="isCreating"
              />

              <!-- Reject Button -->
              <StandardButton
                type="magenta"
                variant="outline"
                size="normal"
                label="Reject"
                @click="handleReject"
              />

              <!-- Next Button -->
              <StandardButton
                type="magenta"
                variant="outline"
                size="normal"
                label=""
                rightIcon="arrowRight"
                @click="handleNext"
                :class="{ 'invisible-btn': currentIndex >= totalCount }"
              />
            </div>
          </div>
        </div>

        <!-- Edit Actions - Edit Mode -->
        <div v-else class="modal-actions">
          <div class="actions-content edit-actions-content">
            <div class="actions-left">
              <AskIQButton label="Improve" :height="40" :disabled="true" />
            </div>

            <div class="actions-right edit-actions-buttons">
              <!-- Discard Button -->
              <StandardButton
                type="magenta"
                variant="outline"
                size="normal"
                label="Discard"
                @click="handleCancelEdit"
              />
              <!-- Create and Attach Button -->
              <StandardButton
                type="magenta"
                variant="primary"
                size="normal"
                label="Create and Attach"
                :disabled="!isFormValid || isCreating"
                :loaderLeft="isCreating"
                @click="handleSaveChanges"
              />
            </div>
          </div>
        </div>

        <!-- Responsibility Field - Navigate Mode -->
        <div
          v-if="!isEditMode && currentResponsibility.name"
          class="modal-content"
        >
          <div class="field-section">
            <div class="field-header">
              <span class="field-label">Responsibility</span>
            </div>
            <span class="field-value">{{ currentResponsibility.name }}</span>
          </div>
        </div>

        <!-- Responsibility Field - Edit Mode -->
        <div v-if="isEditMode" class="modal-content">
          <div class="field-section">
            <ExTextInputWithLimit
              v-model="editedName"
              label="Responsibility"
              placeholder="Enter responsibility name"
              :min="3"
              :max="120"
              :show-char-counter="true"
              :errorMessage="backendErrorMessage"
              class="edit-input"
              @validation-done="handleNameValidation"
            />
          </div>
        </div>

        <!-- Purpose Field - Navigate Mode -->
        <div
          v-if="!isEditMode && currentResponsibility.purpose"
          class="modal-content"
        >
          <div class="field-section">
            <div class="field-header">
              <span class="field-label">Purpose</span>
            </div>
            <div class="field-value-scrollable">
              <span class="field-value field-value-purpose">
                {{ currentResponsibility.purpose }}
              </span>
            </div>
          </div>
        </div>

        <!-- Purpose Field - Edit Mode -->
        <div v-if="isEditMode" class="modal-content">
          <div class="field-section">
            <MemoInputWithLimit
              v-model="editedPurpose"
              label="Purpose"
              placeholder="Enter purpose"
              :min="3"
              :max="275"
              :rows="6"
              :auto-grow="false"
              :show-char-counter="true"
              class="edit-textarea"
              @validation-done="handlePurposeValidation"
            />
          </div>
        </div>
      </template>
    </v-card>
  </v-dialog>

  <!-- Inline mode - render without v-dialog wrapper -->
  <v-card v-else class="responsibility-suggestion-modal" :class="{ 'edit-mode-background': isEditMode }">
    <!-- QuadrilleBackground for Edit Mode -->
    <QuadrilleBackground v-if="isEditMode" className="background-popup" />
    
    <!-- Modal Header -->
    <div class="modal-header">
      <div class="header-content-wrapper">
        <div class="header-content" :class="{ 'edit-mode-header': isEditMode }">
          <!-- Edit Mode Header (Icon before Title) -->
          <template v-if="isEditMode">
            <!-- Featured Icon -->
            <div class="featured-icon">
              <Icon name="org_icon_responsibility" />
            </div>
            <div class="header-text">
              <v-card-title class="modal-title">
                Responsibility Suggestion ({{ currentIndex }}/{{ totalCount }})
              </v-card-title>
            </div>
          </template>
          
          <!-- Navigate Mode Header (Icon before Title) -->
          <template v-else>
            <!-- Featured Icon -->
            <div class="featured-icon">
              <Icon name="org_icon_responsibility" />
            </div>
            <!-- Title and Description -->
            <div class="header-text">
              <v-card-title class="modal-title">
                {{
                  isAllDone
                    ? "All suggestions were processed"
                    : `${totalCount} Suggested ${
                        totalCount === 1 ? "Responsibility" : "Responsibilities"
                      } for Role ${roleName}`
                }}
              </v-card-title>
              <v-card-text class="modal-description">
                {{
                  isAllDone
                    ? "Close the right pane or ask for more suggestions"
                    : description
                }}
              </v-card-text>
            </div>
          </template>
        </div>
      </div>

      <!-- Close Button -->
      <v-btn icon @click="handleClose" color="#98A2B3" class="modal-close-btn-suggestion">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <!-- Bottom spacing -->
      <div class="header-bottom-spacing"></div>
    </div>

    <!-- Completion State (when all responsibilities are done) -->
    <div
      v-if="isAllDone"
      class="completion-state"
    >
      <div class="completion-actions">
        <div class="completion-actions-left">
          <StandardButton
            type="magenta"
            variant="outline"
            size="full-width"
            label="Suggest More"
            @click="handleSuggestMore"
          />
        </div>
        <div class="completion-actions-right">
          <StandardButton
            type="magenta"
            variant="primary"
            size="full-width"
            label="Close"
            @click="handleClose"
          />
        </div>
      </div>
    </div>

    <!-- Normal Content (when there are responsibilities left) -->
    <template v-else>
      <!-- Navigation Actions - Navigate Mode -->
      <div v-if="!isEditMode" class="modal-actions">
        <div class="actions-content">
          <StandardButton
            type="magenta"
            variant="outline"
            size="normal"
            label="Edit"
            @click="handleEdit"
          />
          <div class="actions-left">
            <span class="counter-text"
              >{{ currentIndex }} of {{ totalCount }}</span
            >
          </div>

          <div class="actions-right">
            <!-- Back Button -->
            <StandardButton
              type="magenta"
              variant="outline"
              size="normal"
              label=""
              leftIcon="arrowLeft"
              @click="handlePrevious"
              :class="{ 'invisible-btn': currentIndex <= 1 }"
            />

            <!-- Create and Attach Button -->
            <StandardButton
              type="magenta"
              variant="primary"
              size="normal"
              label="Create and Attach"
              @click="handleCreateAndAttach"
              :loaderLeft="isCreating"
              :disabled="isCreating"
            />

            <!-- Reject Button -->
            <StandardButton
              type="magenta"
              variant="outline"
              size="normal"
              label="Reject"
              @click="handleReject"
            />

            <!-- Next Button -->
            <StandardButton
              type="magenta"
              variant="outline"
              size="normal"
              label=""
              rightIcon="arrowRight"
              @click="handleNext"
              :class="{ 'invisible-btn': currentIndex >= totalCount }"
            />
          </div>
        </div>
      </div>

      <!-- Edit Actions - Edit Mode -->
      <div v-else class="modal-actions">
        <div class="actions-content edit-actions-content">
          <div class="actions-left">
            <!-- <AskIQButton label="Improve" :height="40" :disabled="true" /> -->
          </div>

          <div class="actions-right edit-actions-buttons">
            <!-- Discard Button -->
            <StandardButton
              type="magenta"
              variant="outline"
              size="normal"
              label="Discard"
              @click="handleCancelEdit"
            />
            <!-- Create and Attach Button -->
            <StandardButton
              type="magenta"
              variant="primary"
              size="normal"
              label="Create and Attach"
              :disabled="!isFormValid || isCreating"
              :loaderLeft="isCreating"
              @click="handleSaveChanges"
            />
          </div>
        </div>
      </div>

      <!-- Responsibility Field - Navigate Mode -->
      <div
        v-if="!isEditMode && currentResponsibility.name"
        class="modal-content"
      >
        <div class="field-section">
          <div class="field-header">
            <span class="field-label">Responsibility</span>
          </div>
          <span class="field-value">{{ currentResponsibility.name }}</span>
        </div>
      </div>

      <!-- Responsibility Field - Edit Mode -->
      <div v-if="isEditMode" class="modal-content">
        <div class="field-section">
          <ExTextInputWithLimit
            v-model="editedName"
            label="Responsibility"
            placeholder="Enter responsibility name"
            :min="3"
            :max="120"
            :show-char-counter="true"
            :errorMessage="backendErrorMessage"
            class="edit-input"
            @validation-done="handleNameValidation"
          />
        </div>
      </div>

      <!-- Purpose Field - Navigate Mode -->
      <div
        v-if="!isEditMode && currentResponsibility.purpose"
        class="modal-content"
      >
        <div class="field-section">
          <div class="field-header">
            <span class="field-label">Purpose</span>
          </div>
          <div class="field-value-scrollable">
            <span class="field-value field-value-purpose">
              {{ currentResponsibility.purpose }}
            </span>
          </div>
        </div>
      </div>

      <!-- Purpose Field - Edit Mode -->
      <div v-if="isEditMode" class="modal-content">
        <div class="field-section">
          <MemoInputWithLimit
            v-model="editedPurpose"
            label="Purpose"
            placeholder="Enter purpose"
            :min="3"
            :max="1000"
            :rows="6"
            :auto-grow="true"
            :show-char-counter="true"
            class="edit-textarea"
            @validation-done="handlePurposeValidation"
            :enable-base-validation="false"
          />
        </div>
      </div>
    </template>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import Icon from "@components/icons/Icon.vue";
import ExTextInputWithLimit from "@components/TextInputWithLimit/ExTextInputWithLimit.vue";
import MemoInputWithLimit from "@components/MemoInputWithLimit/MemoInputWithLimit.vue";
import AskIQButton from "@components/AskIQButton/AskIQButton.vue";
import QuadrilleBackground from "@components/svgs/QuadrilleBackground.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    type: [String, Number],
    default: 626,
  },
  attach: {
    type: [String, Boolean],
    default: false,
  },
  inline: {
    type: Boolean,
    default: false,
  },
  roleName: {
    type: String,
    required: true,
  },
  responsibilities: {
    type: Array,
    default: () => [],
    validator: (value) => {
      return value.every(
        (item) =>
          item &&
          typeof item === "object" &&
          "name" in item &&
          "purpose" in item
      );
    },
  },
  description: {
    type: String,
    default:
      "IQ scanned public sources to suggest these Responsibilities. Before suggesting, it looked into currently attached Responsibilities to exclude same or similar from suggestions.",
  },
  isCreating: {
    type: Boolean,
    default: false,
  },
  creationError: {
    type: String,
    default: "",
  },
});

const emit = defineEmits([
  "update:modelValue",
  "close",
  "edit",
  "create-and-attach",
  "reject",
  "previous",
  "next",
  "suggest-more",
  "save-changes",
  "is-all-done",
]);

const isVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

// State management: 'navigate' or 'edit'
const currentState = ref("navigate");
const isEditMode = computed(() => currentState.value === "edit");

// Edit mode fields
const editedName = ref("");
const editedPurpose = ref("");

// Validation state
const isNameValid = ref(true);
const isPurposeValid = ref(true);
const isFormValid = computed(() => isNameValid.value && isPurposeValid.value);

// Backend error state
const backendErrorMessage = ref("");

const currentIndex = ref(1);
// Internal copy of responsibilities to handle removal locally
const internalResponsibilities = ref([...props.responsibilities]);
const totalCount = computed(() => internalResponsibilities.value.length);
const isProcessing = ref(false);

const currentResponsibility = computed(() => {
  const listLength = internalResponsibilities.value.length;
  if (listLength === 0) {
    return { name: "", purpose: "" };
  }
  // Ensure index is within bounds
  if (currentIndex.value > listLength) {
    currentIndex.value = listLength;
  }
  
  const original = internalResponsibilities.value[currentIndex.value - 1] || {
    name: "",
    purpose: "",
  };
  
  // If in edit mode, return the edited values
  if (isEditMode.value && editedName.value && editedPurpose.value) {
    return {
      ...original,
      name: editedName.value,
      purpose: editedPurpose.value,
    };
  }
  
  return original;
});

// Check if all responsibilities are done (list is empty)
const isAllDone = computed(() => {
  return totalCount.value === 0;
});

// Watch isAllDone and emit event when it changes
watch(isAllDone, (newValue) => {
  emit("is-all-done", newValue);
}, { immediate: true });

// Expose isAllDone to parent component
defineExpose({
  isAllDone,
});

// Watch for changes in responsibilities from props (when parent updates the list)
watch(
  () => props.responsibilities,
  (newList, oldList) => {
    if (newList.length === 0) {
      internalResponsibilities.value = [];
      return;
    }

    // If we're processing, don't update from props - we're managing it internally
    if (isProcessing.value) {
      isProcessing.value = false;
      return;
    }

    // Update internal list when props change (but not during processing)
    internalResponsibilities.value = [...newList];

    // Reset to first item if it's a new list
    const isNewList =
      !oldList || oldList.length === 0 || newList.length !== oldList.length;
    if (newList.length > 0 && isNewList) {
      currentIndex.value = 1;
    }

    // Adjust index if it's out of bounds
    if (currentIndex.value > newList.length) {
      currentIndex.value = newList.length;
    }
  },
  { immediate: true }
);

// Watch for changes in current index to reset edit mode
watch(currentIndex, () => {
  if (isEditMode.value) {
    currentState.value = "navigate";
    editedName.value = "";
    editedPurpose.value = "";
    isNameValid.value = true;
    isPurposeValid.value = true;
    backendErrorMessage.value = "";
  }
});

// Watch for modal visibility to reset edit mode when closed
watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue && isEditMode.value) {
      currentState.value = "navigate";
      editedName.value = "";
      editedPurpose.value = "";
      isNameValid.value = true;
      isPurposeValid.value = true;
      backendErrorMessage.value = "";
    }
  }
);

// Watch for changes in edited name to clear backend error
watch(editedName, () => {
  if (backendErrorMessage.value) {
    backendErrorMessage.value = "";
  }
});

// Watch for isCreating prop to handle navigation after creation completes
const wasCreating = ref(false);
const indexBeingCreated = ref(null);
watch(
  () => props.isCreating,
  async (newValue, oldValue) => {
    // When creation starts (was false, now true)
    if (oldValue === false && newValue === true) {
      wasCreating.value = true;
      indexBeingCreated.value = currentIndex.value; // Store the index being created
      backendErrorMessage.value = ""; // Clear any previous error
    }
    
    // When creation completes (was true, now false)
    if (oldValue === true && newValue === false) {
      // Use nextTick to ensure creationError prop is updated
      await nextTick();
      
      wasCreating.value = false;
      
      const createdIndex = indexBeingCreated.value;
      indexBeingCreated.value = null;
      
      // Check if there's an error from the backend
      if (props.creationError) {
        // There's an error - stay in edit mode and show the error
        backendErrorMessage.value = props.creationError;
        // Don't remove the item, don't navigate, stay in edit mode
        return;
      }
      
      // No error - proceed with success flow
      // If we were in edit mode during creation, go back to navigate mode
      if (isEditMode.value) {
        currentState.value = "navigate";
        editedName.value = "";
        editedPurpose.value = "";
        isNameValid.value = true;
        isPurposeValid.value = true;
      }
      
      // Now handle the navigation to next item
      const currentTotal = internalResponsibilities.value.length;
      
      // Remove the item from the list
      if (currentTotal > 0) {
        const indexToRemove = createdIndex - 1;
        internalResponsibilities.value.splice(indexToRemove, 1);
        
        // Navigate after removal
        if (internalResponsibilities.value.length === 0) {
          // No more responsibilities - show completion state
          return;
        } else if (createdIndex === currentTotal) {
          // Was at the last item, move to previous (now the new last item)
          currentIndex.value = internalResponsibilities.value.length;
        }
        // else: Not at the last item, stay at current index (now points to next item)
      }
    }
  }
);

const handleClose = () => {
  emit("close");
  emit("update:modelValue", false);
};

const handleSuggestMore = () => {
  emit("suggest-more");
};

const handleEdit = () => {
  // Switch to edit mode and populate edit fields
  currentState.value = "edit";
  editedName.value = currentResponsibility.value.name || "";
  editedPurpose.value = currentResponsibility.value.purpose || "";
  // Initialize validation state as valid since we're loading existing data
  isNameValid.value = true;
  isPurposeValid.value = true;
  // Clear any backend error
  backendErrorMessage.value = "";
};

const handleCancelEdit = () => {
  // Switch back to navigate mode without saving
  currentState.value = "navigate";
  editedName.value = "";
  editedPurpose.value = "";
  // Reset validation state
  isNameValid.value = true;
  isPurposeValid.value = true;
  // Clear any backend error
  backendErrorMessage.value = "";
};

const handleNameValidation = (isValid) => {
  isNameValid.value = isValid;
};

const handlePurposeValidation = (isValid) => {
  isPurposeValid.value = isValid;
};

const handleSaveChanges = () => {
  // Only proceed if form is valid
  if (!isFormValid.value) return;

  // Update the internal responsibility with edited values
  const indexToUpdate = currentIndex.value - 1;
  if (indexToUpdate >= 0 && indexToUpdate < internalResponsibilities.value.length) {
    internalResponsibilities.value[indexToUpdate] = {
      ...internalResponsibilities.value[indexToUpdate],
      name: editedName.value,
      purpose: editedPurpose.value,
    };
  }

  // DON'T switch back to navigate mode yet - stay in edit mode
  // The watcher for isCreating will handle the transition after success

  // Emit save-changes event with updated data
  emit("save-changes", {
    index: currentIndex.value,
    responsibility: internalResponsibilities.value[indexToUpdate],
  });

  // Now trigger the create and attach flow with the updated responsibility
  handleCreateAndAttach();
};

const handleCreateAndAttach = () => {
  const createdIndex = currentIndex.value;

  // Set flag to prevent watch from resetting index
  isProcessing.value = true;

  // Emit event for parent to handle
  // DON'T remove the item immediately - wait for isCreating to become false
  emit("create-and-attach", {
    index: createdIndex,
    responsibility: currentResponsibility.value,
  });

  // The watcher for isCreating will handle:
  // 1. Removing the item from the list
  // 2. Navigating to the next item
  // 3. Exiting edit mode if needed
  
  // Reset flag after emitting
  isProcessing.value = false;
};

const handleReject = () => {
  const rejectedIndex = currentIndex.value;
  const currentTotal = internalResponsibilities.value.length;

  // Set flag to prevent watch from resetting index
  isProcessing.value = true;

  // Emit event for parent to handle
  emit("reject", {
    index: rejectedIndex,
    responsibility: currentResponsibility.value,
  });

  // Handle removal internally - remove the item from the list
  if (currentTotal === 0) {
    return;
  }

  // Remove the rejected item (convert to 0-based index)
  const indexToRemove = rejectedIndex - 1;
  internalResponsibilities.value.splice(indexToRemove, 1);

  // Skip to next responsibility after rejection
  if (internalResponsibilities.value.length === 0) {
    // No more responsibilities - show completion state
    return;
  } else if (rejectedIndex === currentTotal) {
    // Was at the last item, move to previous (now the new last item)
    currentIndex.value = internalResponsibilities.value.length;
  } else {
    // Not at the last item, stay at current index
    // The current index now points to what was the next item
    // No change needed - currentIndex stays the same
  }

  // Reset flag after handling
  isProcessing.value = false;
};

const handlePrevious = () => {
  if (currentIndex.value > 1) {
    // Simple navigation to previous item
    currentIndex.value--;

    emit("previous", {
      index: currentIndex.value,
      responsibility: currentResponsibility.value,
    });
  }
};

const handleNext = () => {
  if (currentIndex.value < totalCount.value) {
    // Simple navigation to next item
    currentIndex.value++;

    emit("next", {
      index: currentIndex.value,
      responsibility: currentResponsibility.value,
    });
  }
};
</script>

<style scoped>
@import "./responsibilitySuggestionModal.css";
</style>
